/**
 * Data hooks for the Dashboard's operator announcements block (#490).
 *
 *   useAnnouncements()        — GET /api/announcements (the feed, display-ordered
 *                               by the worker: pinned first, then newest).
 *   useCreateAnnouncement()   — POST a new announcement.
 *   useUpdateAnnouncement()   — PUT an edit by id.
 *   useDeleteAnnouncement()   — DELETE by id.
 *
 * The mutations invalidate ["announcements"] so the feed refreshes after a post,
 * edit, or delete. The read validates against the shared schema (the payload is
 * operator-authored, so a contract check is cheap insurance). No polling — the
 * app-wide refetchOnWindowFocus brings a co-operator's new post in when the tab
 * regains focus.
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AnnouncementsResponseSchema,
  type Announcement,
  type AnnouncementInput,
  type AnnouncementsResponse,
} from "@command-center/shared";
import { apiDelete, apiGet, apiPost, apiPut } from "./api";

export function useAnnouncements() {
  return useQuery<AnnouncementsResponse, Error>({
    queryKey: ["announcements"],
    queryFn: async () => {
      const raw = await apiGet<unknown>("/api/announcements");
      const parsed = AnnouncementsResponseSchema.safeParse(raw);
      if (!parsed.success) {
        throw new Error(
          `/api/announcements failed schema validation: ${parsed.error.message}`,
        );
      }
      return parsed.data;
    },
  });
}

export function useCreateAnnouncement() {
  const qc = useQueryClient();
  return useMutation<{ ok: true; item: Announcement }, Error, AnnouncementInput>({
    mutationFn: (input) =>
      apiPost<{ ok: true; item: Announcement }>("/api/announcements", input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["announcements"] }),
  });
}

export function useUpdateAnnouncement() {
  const qc = useQueryClient();
  return useMutation<
    { ok: true; item: Announcement },
    Error,
    { id: string; input: AnnouncementInput }
  >({
    mutationFn: ({ id, input }) =>
      apiPut<{ ok: true; item: Announcement }>(
        `/api/announcements/${id}`,
        input,
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["announcements"] }),
  });
}

export function useDeleteAnnouncement() {
  const qc = useQueryClient();
  return useMutation<{ ok: true }, Error, string>({
    mutationFn: (id) => apiDelete<{ ok: true }>(`/api/announcements/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["announcements"] }),
  });
}
